import { z } from "zod";

import {
	CreateLanguageRule,
	LanguageRuleCondition,
	LanguageRuleInfo,
	ReorderLanguageRules,
	UpdateLanguageRule,
} from "../../../../schema/languageRule";

let uidCounter = 0;

export const generateUid = (): string => `draft-${++uidCounter}`;

export interface DraftCondition {
	uid: string;
	field: "profile_language" | "personal_country";
	operator: "equals" | "is_on";
	value: string | string[];
}

export interface DraftRule {
	uid: string;
	id: number | null;
	conditions: DraftCondition[];
	outputLanguage: string;
}

export const toDraft = (rules: LanguageRuleInfo[]): DraftRule[] => {
	return rules.map((rule) => ({
		uid: generateUid(),
		id: rule.id,
		conditions: rule.conditions.map((c: LanguageRuleCondition) => ({
			uid: generateUid(),
			field: c.field,
			operator: c.operator,
			value: c.value,
		})),
		outputLanguage: rule.outputLanguage,
	}));
};

export const toCreatePayload = (draft: DraftRule, priority: number): CreateLanguageRule => ({
	priority,
	conditions: draft.conditions.map((c) => ({
		field: c.field,
		operator: c.operator,
		value: c.value,
	})) as LanguageRuleCondition[],
	outputLanguage: draft.outputLanguage,
});

const canonicalizeConditions = (
	conditions: Pick<LanguageRuleCondition, "field" | "operator" | "value">[]
): string =>
	JSON.stringify(
		conditions
			.map((c) => ({
				field: c.field,
				operator: c.operator,
				value: Array.isArray(c.value) ? [...c.value].sort() : c.value,
			}))
			.sort((a, b) => {
				if (a.field !== b.field) {
					return a.field < b.field ? -1 : 1;
				}

				if (a.operator !== b.operator) {
					return a.operator < b.operator ? -1 : 1;
				}

				return JSON.stringify(a.value) < JSON.stringify(b.value) ? -1 : 1;
			})
	);

export const toUpdatePayload = (
	server: LanguageRuleInfo,
	draft: DraftRule
): UpdateLanguageRule | null => {
	const body: UpdateLanguageRule = {};
	let changed = false;

	if (draft.outputLanguage !== server.outputLanguage) {
		body.outputLanguage = draft.outputLanguage;
		changed = true;
	}

	const conditionsChanged =
		canonicalizeConditions(draft.conditions) !== canonicalizeConditions(server.conditions);

	if (conditionsChanged) {
		body.conditions = draft.conditions.map((c) => ({
			field: c.field,
			operator: c.operator,
			value: c.value,
		})) as LanguageRuleCondition[];
		changed = true;
	}

	return changed ? body : null;
};

export const diffRules = (
	server: LanguageRuleInfo[],
	draft: DraftRule[]
): {
	toCreate: CreateLanguageRule[];
	toUpdate: { id: number; body: UpdateLanguageRule }[];
	toDelete: number[];
	reorderIds: number[];
} => {
	const serverMap = new Map(server.map((r) => [r.id, r]));
	const draftIds = new Set(draft.map((d) => d.id).filter((id): id is number => id !== null));

	const toCreate: CreateLanguageRule[] = [];
	const toUpdate: { id: number; body: UpdateLanguageRule }[] = [];
	const reorderIds: number[] = [];

	draft.forEach((d, index) => {
		if (d.id === null) {
			toCreate.push(toCreatePayload(d, index));
		} else {
			const s = serverMap.get(d.id);

			if (s) {
				const body = toUpdatePayload(s, d);

				if (body) {
					toUpdate.push({ id: d.id, body });
				}

				reorderIds.push(d.id);
			}
		}
	});

	const toDelete = server.filter((s) => !draftIds.has(s.id)).map((s) => s.id);

	return { toCreate, toUpdate, toDelete, reorderIds };
};

export const buildReorderPayload = (allIds: number[]): ReorderLanguageRules => ({
	items: allIds.map((id, priority) => ({ id, priority })),
});

export const DraftConditionSchema = z.object({
	uid: z.string(),
	field: z.enum(["profile_language", "personal_country"]),
	operator: z.enum(["equals", "is_on"]),
	value: z.union([z.string(), z.array(z.string())]),
});

export const DraftRuleSchema = z.object({
	uid: z.string(),
	id: z.number().nullable(),
	conditions: z
		.array(DraftConditionSchema)
		.min(1, { message: "At least one condition is required" }),
	outputLanguage: z.string().min(1, { message: "Output language is required" }),
});

export const DraftFormSchema = z.object({
	rules: z.array(DraftRuleSchema),
	defaultOutputLanguage: z.string().nullable(),
});

export type DraftFormValues = z.infer<typeof DraftFormSchema>;

export const makeEmptyCondition = (
	field: DraftCondition["field"] = "profile_language"
): DraftCondition => ({
	uid: generateUid(),
	field,
	operator: field === "profile_language" ? "equals" : "is_on",
	value: field === "profile_language" ? "" : [],
});

export const makeEmptyRule = (): DraftRule => ({
	uid: generateUid(),
	id: null,
	conditions: [makeEmptyCondition()],
	outputLanguage: "",
});
